import { useMemo, type FC, type RefObject } from 'react';
import { _isNil, _notNil } from '@/littledash';

import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import type { PlannedAnimalDefinition } from '@/components/Animals/PlannedAnimals/PlannedAnimals.utils';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';

import SpreadsheetV16, { type SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16';
import {
  type MapAnimalRow,
  mapAnimalsColumnDataV16,
  mapAnimalsSettingsV16,
  createPlannedIdCellsConfigV16,
  processDataChangeV16,
} from '@/components/Animals/MapAnimals.utils';

interface MapAnimalsTableV16Props {
  mapData: Array<MapAnimalRow>;
  setMapData: (data: Array<MapAnimalRow>) => void;
  groups: Array<TreatmentGroup>;
  plannedAnimals: Array<PlannedAnimalDefinition>;
  innerRef: RefObject<HotTableRef | null>;
  setInvalid: (invalid: boolean) => void;
}

const MapAnimalsTableV16: FC<MapAnimalsTableV16Props> = ({
  mapData,
  setMapData,
  groups,
  plannedAnimals,
  innerRef,
  setInvalid,
}) => {
  const columnData = useMemo(() => {
    return mapAnimalsColumnDataV16(groups, innerRef);
  }, [groups, innerRef]);

  const settings = useMemo<SpreadsheetV16Settings>(() => mapAnimalsSettingsV16(columnData), [columnData]);

  const cells = useMemo(
    () => createPlannedIdCellsConfigV16(mapData, groups, plannedAnimals),
    [mapData, groups, plannedAnimals]
  );

  const handleAfterChange = (changes: Array<[number, string | number, unknown, unknown]> | null, source: string) => {
    if (_isNil(changes) || source === 'loadData') {
      return;
    }
    const hotInstance = innerRef.current?.hotInstance ?? null;
    if (_notNil(hotInstance)) {
      const updatedMapData = processDataChangeV16(hotInstance, plannedAnimals);
      setMapData(updatedMapData);
    }
  };

  return (
    <SpreadsheetV16
      data={mapData as unknown as Array<Record<string, unknown>>}
      settings={settings}
      innerRef={innerRef as RefObject<HotTableRef>}
      className="mapAnimals"
      buttonClasses="absolute-sheet-buttons-v16"
      setInvalid={setInvalid}
      afterChange={handleAfterChange}
      addCol={false}
      testPrefix="map-animals-v16"
      cells={cells}
    />
  );
};

export default MapAnimalsTableV16;
